import { createHttpRemote, OfflineError, type Remote } from '@quickreport/sync';

/** The API address. In development this is the local server; set VITE_API_URL to change it. */
export let API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

let tokenProvider: () => string | null = () => null;

/** The desktop app has no cookie jar for the API; it signs requests with a bearer token. */
export function configureApi(o: { baseUrl?: string; getToken?: () => string | null }) {
  if (o.baseUrl) API_URL = o.baseUrl;
  if (o.getToken) tokenProvider = o.getToken;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** JSON request to the API. Throws OfflineError when the network is unreachable. */
export async function api<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  const token = tokenProvider();
  if (token) headers.authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new OfflineError();
  }
  if (!res.ok) {
    let p: { code?: string; title?: string; details?: unknown } = {};
    try {
      p = await res.json();
    } catch {
      // not JSON
    }
    throw new ApiRequestError(
      res.status,
      p.code ?? 'http_error',
      p.title ?? `Request failed (${res.status})`,
      p.details,
    );
  }
  return (await res.json()) as T;
}

export function createRemote(): Remote {
  return createHttpRemote({
    baseUrl: API_URL,
    getToken: () => tokenProvider(),
  });
}

/** A message a person can act on, from any error we throw. */
export function describeError(err: unknown): string {
  if (err instanceof OfflineError) return 'You are offline. Connect to the internet and try again.';
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Try again.';
}
